import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ImageMock from "../assets/images/image-mock.png";
import Dropdown from "../components/DropDown";
import { MenuButton } from "@headlessui/react";
import { FaBars, FaHeart, FaRegHeart } from "react-icons/fa";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";


const VotingCard = ({ participant, onVote, disabled = false, isWinner = false }) => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  // Check if this image is already favorited
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!participant?.body_part_image_id || !profile?.accessToken) return;
      
      try {
        const response = await fetch(`${API_BASE}accounts/favorites/`, {
          headers: {
            'Authorization': `Bearer ${profile.accessToken}`,
          },
        });
        
        if (response.ok) {
          const favorites = await response.json();
          const isFav = favorites.some(fav => fav.body_part_image_id === participant.body_part_image_id);
          setIsFavorited(isFav);
        }
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    checkFavoriteStatus();
  }, [participant?.body_part_image_id, profile?.accessToken]);

  const handleCardClick = () => {
    if (!disabled && onVote) {
      onVote();
    }
  };
  
  const getContributorName = () => (
    participant?.contributor_name ||
    participant?.screen_name ||
    participant?.name ||
    "Contributor"
  );

  const handleVisitProfile = () => {
    if (!participant?.contributor_id) {
      toast.error("Contributor profile is not available for this image");
      return;
    }

    const contributorName = getContributorName();
    navigate(`/explore-content?search=${encodeURIComponent(contributorName)}`, {
      state: {
        selectedContributor: {
          id: participant.contributor_id,
          name: contributorName,
          screen_name: contributorName,
          img: participant.image_url || ImageMock,
          profile_picture: participant.image_url || ImageMock,
        },
      },
    });
  };

  const handleFlagImage = async () => {
    if (!profile?.accessToken) {
      toast.error("Please log in to flag content.");
      return;
    }

    const contributorName = getContributorName();
    const payload = {
      target_type: "content",
      reason: "User flagged this contest image for admin review.",
      image_url: participant?.image_url || "",
      metadata: {
        contributor_name: contributorName,
        contributor_id: participant?.contributor_id || null,
        contest_participant_id: participant?.id || null,
        body_part_image_id: participant?.body_part_image_id || null,
        image_url: participant?.image_url || null,
      },
    };

    if (participant?.body_part_image_id) payload.body_part_image_id = participant.body_part_image_id;
    if (participant?.contributor_id) payload.contributor_id = participant.contributor_id;
    if (participant?.id) payload.contest_participant_id = participant.id;
    if (participant?.contest_id) payload.contest_id = participant.contest_id;

    try {
      const response = await fetch(`${API_BASE}accounts/content-flags/`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${profile.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to submit flag.");
      }

      toast.success("Flag submitted to Content Moderation.");
    } catch (error) {
      console.error("Error submitting content flag:", error);
      toast.error(error.message || "Failed to submit flag.");
    }
  };

  const handleFavoriteToggle = async (e) => {
    // Prevent vote from being triggered
    e.stopPropagation();
    
    if (!participant?.body_part_image_id) {
      toast.error("Cannot favorite this image");
      return;
    }

    if (!profile?.accessToken) {
      toast.error("Please login to favorite images");
      return;
    }

    if (isTogglingFavorite) return; // Prevent double-clicking

    try {
      setIsTogglingFavorite(true);
      
      const response = await fetch(`${API_BASE}accounts/favorites/toggle/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${profile.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          body_part_image_id: participant.body_part_image_id,
        }),
      });

      if (response.ok) {
        const newFavState = !isFavorited;
        setIsFavorited(newFavState);
        toast.success(newFavState ? '❤️ Added to favorites!' : 'Removed from favorites');
      } else {
        toast.error('Failed to update favorites');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    } finally {
      setIsTogglingFavorite(false);
    }
  };
  
  if (!participant) {
    return null;
  }
  
  return (
    <div className="w-full">
      {/* Contributor Name - Above Card */}
      {participant.contributor_name && (
        <div className="mb-3 text-center">
          <h3 className="text-xl font-bold text-[#ceb46a]">
            {participant.contributor_name}
          </h3>
        </div>
      )}
      
      <div
        className={`relative overflow-hidden rounded-xl bg-black border-2 shadow-2xl h-[360px] sm:h-[420px] md:h-[520px] w-full transition-all ${
          disabled
            ? "opacity-50 cursor-not-allowed border-gray-700"
            : "cursor-pointer hover:border-[#ceb46a] hover:shadow-[0_0_30px_rgba(206,180,106,0.3)] border-gray-700"
        } ${isWinner ? "border-[#ceb46a] shadow-[0_0_40px_rgba(206,180,106,0.5)] scale-[1.02]" : ""}`}
        onClick={handleCardClick}
      >
        {/* Background Image */}
        <img
          className="absolute inset-0 w-full h-full object-cover"
          src={participant.image_url || ImageMock}
          alt={participant.contributor_name || "content"}
          onError={(e) => {
            e.target.src = ImageMock;
          }}
        />

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent pointer-events-none"></div>

        {/* Winner Badge */}
        {isWinner && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#ceb46a] to-[#d4bb6f] text-black px-6 py-2 rounded-full font-bold text-sm shadow-lg z-20 flex items-center gap-2">
            <span>🏆</span>
            WINNER
          </div>
        )}

        {/* Top Controls */}
        <div
          className="absolute top-4 left-4 right-4 flex items-center justify-between z-20"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            triggerButton={
              <MenuButton className="p-2 rounded-xl bg-black/60 hover:bg-[#ceb46a]/30 text-white transition-colors backdrop-blur">
                <FaBars size={26} />
              </MenuButton>
            }
            items={[
              {
                label: "Visit Profile",
                value: "visit-profile",
                onClick: handleVisitProfile,
              },
              {
                label: "Flag",
                value: "flag-review",
                onClick: handleFlagImage,
              },
            ]}
          />

          <button
            onClick={handleFavoriteToggle}
            disabled={isTogglingFavorite}
            className={`p-2 rounded-full bg-black/60 backdrop-blur transition-all ${
              isTogglingFavorite ? "opacity-50 cursor-wait" : "hover:bg-[#ceb46a]/30"
            }`}
            title={isFavorited ? "Remove from favorites" : "Add to favorites"}
          >
            {isFavorited ? (
              <FaHeart size={24} className="text-[#ea4335]" />
            ) : (
              <FaRegHeart size={24} className="text-white hover:text-[#ea4335]" />
            )}
          </button>
        </div>

        {/* Bottom Message */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
          <div className="bg-black/60 backdrop-blur rounded-lg px-2 py-1 border border-[#ceb46a]/40">
            <p className="text-white font-semibold text-center text-xs">
              Click Image to Vote
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VotingCard;
